/**
 * HTML / Markdown 白名单净化，用于拦截 Vditor/Lute 黑名单 sanitize 的绕过向量
 * （iframe srcdoc、javascript: 协议、marquee onstart 等）
 */

const ALLOWED_TAGS = new Set([
  "a",
  "abbr",
  "audio",
  "b",
  "bdi",
  "bdo",
  "blockquote",
  "br",
  "caption",
  "code",
  "col",
  "colgroup",
  "del",
  "details",
  "div",
  "dl",
  "dt",
  "dd",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "input",
  "ins",
  "kbd",
  "li",
  "mark",
  "ol",
  "p",
  "pre",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "small",
  "source",
  "span",
  "strong",
  "sub",
  "sup",
  "summary",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
  "video",
]);

const ALLOWED_ATTRS = new Set([
  "alt",
  "checked",
  "class",
  "colspan",
  "controls",
  "disabled",
  "height",
  "href",
  "id",
  "open",
  "poster",
  "rel",
  "rowspan",
  "src",
  "start",
  "target",
  "title",
  "type",
  "width",
]);

const URL_ATTRS = new Set(["href", "src"]);

/**
 * 校验 URL 协议：仅放行 http(s)/mailto/tel/ftp、安全位图 data: 与相对路径
 */
export function isSafeUrl(url) {
  if (url == null || url === "") {
    return true;
  }
  const decodeEl = document.createElement("textarea");
  decodeEl.innerHTML = String(url);
  const normalized = decodeEl.value
    .replace(/[\x00\t\n\r]/g, "")
    .replace(/^[\x00-\x20]+/, "")
    .replace(/[\x00-\x20]+$/, "");
  if (!normalized) {
    return true;
  }
  // 协议相对 URL 按 http(s) 资源处理
  if (/^\/\//.test(normalized)) {
    return true;
  }
  const schemeMatch = normalized.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (!schemeMatch) {
    return true;
  }
  const scheme = schemeMatch[1].toLowerCase();
  if (["http", "https", "mailto", "tel", "ftp"].indexOf(scheme) > -1) {
    return true;
  }
  if (scheme === "data") {
    return /^data:image\/(png|jpe?g|gif|webp|bmp)/i.test(normalized);
  }
  return false;
}

function sanitizeElement(root) {
  const children = Array.from(root.children);
  children.forEach((child) => {
    sanitizeElement(child);
    const tag = child.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      const parent = child.parentNode;
      if (!parent) {
        return;
      }
      while (child.firstChild) {
        parent.insertBefore(child.firstChild, child);
      }
      parent.removeChild(child);
      return;
    }
    Array.from(child.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || !ALLOWED_ATTRS.has(name)) {
        child.removeAttribute(attr.name);
        return;
      }
      if (URL_ATTRS.has(name) && !isSafeUrl(attr.value)) {
        child.removeAttribute(attr.name);
      }
    });
    if (
      tag === "a" &&
      child.getAttribute("target") === "_blank" &&
      !child.getAttribute("rel")
    ) {
      child.setAttribute("rel", "noopener noreferrer");
    }
    // 任务列表 checkbox 仅允许只读展示
    if (tag === "input") {
      const inputType = (child.getAttribute("type") || "").toLowerCase();
      if (inputType !== "checkbox") {
        const parent = child.parentNode;
        if (parent) {
          parent.removeChild(child);
        }
        return;
      }
      child.setAttribute("disabled", "disabled");
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (["type", "checked", "disabled", "class"].indexOf(name) === -1) {
          child.removeAttribute(attr.name);
        }
      });
    }
  });
}

/**
 * 对 HTML 字符串做标签/属性/URL 白名单净化（DOMParser 惰性文档，不执行脚本）
 */
export function sanitizeHtml(dirty) {
  if (typeof dirty !== "string" || !dirty) {
    return "";
  }
  const doc = new DOMParser().parseFromString(dirty, "text/html");
  sanitizeElement(doc.body);
  return doc.body.innerHTML;
}

/**
 * 渲染前对 markdown 源码做轻量危险标签剥离（防御纵深，主防线仍是 sanitizeHtml）
 */
export function sanitizeMarkdownSource(content) {
  if (typeof content !== "string" || !content) {
    return "";
  }
  let text = content;
  // 连同内容移除 script/style/iframe
  text = text.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "");
  text = text.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "");
  text = text.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, "");
  text = text.replace(/<\/?(?:script|style|iframe|object|embed|form|base|meta|link|svg|math|marquee|frameset|frame)\b[^>]*>/gi, "");
  // markdown 行内链接危险协议退化为纯文本
  text = text.replace(
    /(!?)\[([^\]]*)\]\(\s*(<[^>]*>|[^)\s]*)[^)]*\)/g,
    (link, bang, linkText, dest) => {
      if (isSafeUrl(String(dest).replace(/^<|>$/g, ""))) {
        return link;
      }
      return bang ? "" : linkText;
    }
  );
  text = text.replace(/^\[[^\]]+\]:\s*(\S+).*$/gm, (line, dest) => {
    return isSafeUrl(dest) ? line : "";
  });
  return text;
}
